// ── 홍보물 관리 — 메인 페이지 상태 + 핸들러 훅 ───────────────────────────────
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { toDateTimeString } from '../../../utils/date';
import { MOBILE_BREAKPOINT } from './utils';
import { HQ_PROMO_API } from './promoApi';

// api: 주소 묶음(promoApi.js, 2026-10-19) — 기본은 본사 주소
export function usePromotionalManagement(api = HQ_PROMO_API) {
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // 데이터
  const [orders, setOrders]                           = useState([]);
  const [promotionalItemsList, setPromotionalItemsList] = useState([]);
  const [categoriesList, setCategoriesList]           = useState([]);   // product_categories 전체
  const [loadingOrders, setLoadingOrders]             = useState(false);
  // 회사별·통합 화면(5-6): 이 화면 회사들·회사 정하기 가능(서버 목록 응답 screen_codes·can_assign_company). 본사는 비어 있다.
  const [screenMeta, setScreenMeta]                   = useState({ codes: [], canAssign: false });

  // 모달
  const [itemManageOpen, setItemManageOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editTarget, setEditTarget]         = useState(null);   // null = 신규, object = 수정

  // 삭제 확인
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // 이력 모달
  const [historyOrder, setHistoryOrder]     = useState(null);  // 이력 볼 주문 객체
  const [historyData, setHistoryData]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 검색어
  const [searchQuery, setSearchQuery] = useState('');

  // 현재 로그인 사용자 정보 (발주담당자 기록용)
  const [currentUser, setCurrentUser] = useState(null);

  // 택배정보 인라인 편집 — { orderId, itemId, courier, trackingNumber }
  const [shippingEdit, setShippingEdit] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    const res = await apiClient.get(api.orders);
    setLoadingOrders(false);
    if (!res.ok) { showToast('목록 조회 실패', 'error'); return; }
    setOrders(Array.isArray(res.data?.data) ? res.data.data : []);
    setScreenMeta({ codes: Array.isArray(res.data?.screen_codes) ? res.data.screen_codes : [], canAssign: Boolean(res.data?.can_assign_company) });
  }, [showToast, api.orders]);

  const fetchActiveItems = useCallback(async () => {
    const res = await apiClient.get(api.itemsActive);
    if (res.ok) setPromotionalItemsList(Array.isArray(res.data?.data) ? res.data.data : []);
  }, [api.itemsActive]);

  // product_categories 전체 목록 (하드코딩 없이 동적 로드)
  const fetchCategories = useCallback(async () => {
    const res = await apiClient.get(api.productCategories);
    if (res.ok) setCategoriesList(Array.isArray(res.data?.data) ? res.data.data : []);
  }, [api.productCategories]);

  useEffect(() => {
    fetchOrders();
    fetchActiveItems();
    fetchCategories();
  }, [fetchOrders, fetchActiveItems, fetchCategories]);

  // 로그인 사용자 정보 1회 조회
  useEffect(() => {
    apiClient.get('/api/me').then((res) => {
      if (res.ok) setCurrentUser(res.data?.user || null);
    });
  }, []);

  // 검색 필터링 — 상호, 대표자, 담당자, 전화, 홍보물명, 카테고리
  const filteredOrders = searchQuery.trim()
    ? orders.filter((o) => {
        const q = searchQuery.trim().toLowerCase();
        if ((o.company_name     || '').toLowerCase().includes(q)) return true;
        if ((o.ceo_name         || '').toLowerCase().includes(q)) return true;
        if ((o.sales_person_name|| '').toLowerCase().includes(q)) return true;
        if ((o.phone            || '').toLowerCase().includes(q)) return true;
        if ((o.mobile_phone     || '').toLowerCase().includes(q)) return true;
        if ((o.category_name    || '').toLowerCase().includes(q)) return true;
        if (Array.isArray(o.items) && o.items.some((i) => (i.item_name || '').toLowerCase().includes(q))) return true;
        return false;
      })
    : orders;

  // 배송불필요 아이템 업무 단계 변경 (순차, custom_steps 전용) — 낙관적 업데이트
  async function handleItemStepChange(orderId, itemId, stepId) {
    // 낙관적 업데이트: 해당 아이템의 workflow_step_id를 즉시 반영
    setOrders((prev) => prev.map((o) => {
      if (o.id !== orderId) return o;
      return {
        ...o,
        items: (o.items || []).map((i) =>
          i.id === itemId ? { ...i, workflow_step_id: stepId } : i
        ),
      };
    }));
    const res = await apiClient.patch(
      `${api.orders}/${orderId}/items/${itemId}/step`,
      { workflow_step_id: stepId }
    );
    if (!res.ok) {
      showToast('단계 변경 실패', 'error');
      fetchOrders();
    }
  }

  // 발주 체크박스 토글 (is_ordered, ordered_by 기록) — 낙관적 업데이트
  async function handleItemOrderToggle(orderId, itemId, currentIsOrdered) {
    const newIsOrdered = !currentIsOrdered;
    const callerName   = currentUser?.name || '관리자';
    // 낙관적 업데이트
    setOrders((prev) => prev.map((o) => {
      if (o.id !== orderId) return o;
      return {
        ...o,
        items: (o.items || []).map((i) => {
          if (i.id !== itemId) return i;
          return {
            ...i,
            is_ordered:  newIsOrdered,
            ordered_by:  newIsOrdered ? callerName : null,
            // toDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
            ordered_at:  newIsOrdered ? toDateTimeString(Date.now()) : null,
          };
        }),
      };
    }));
    const res = await apiClient.patch(
      `${api.orders}/${orderId}/items/${itemId}/order-toggle`,
      { is_ordered: newIsOrdered }
    );
    if (!res.ok) {
      showToast('발주 처리 실패', 'error');
      fetchOrders();
    }
  }

  // 아이템 취소/복원 토글 — 낙관적 업데이트
  async function handleItemCancel(orderId, itemId) {
    setOrders((prev) => prev.map((o) => {
      if (o.id !== orderId) return o;
      return {
        ...o,
        items: (o.items || []).map((i) =>
          i.id === itemId ? { ...i, is_cancelled: !i.is_cancelled } : i
        ),
      };
    }));
    const res = await apiClient.patch(
      `${api.orders}/${orderId}/items/${itemId}/cancel`, {}
    );
    if (!res.ok) {
      showToast('취소 처리 실패', 'error');
      fetchOrders();
    }
  }

  // 이력 모달 열기
  async function openHistory(order) {
    setHistoryOrder(order);
    setHistoryData([]);
    setHistoryLoading(true);
    const res = await apiClient.get(`${api.orders}/${order.id}/history`);
    setHistoryLoading(false);
    if (res.ok) setHistoryData(Array.isArray(res.data?.data) ? res.data.data : []);
    else showToast('이력 조회 실패', 'error');
  }

  // 택배정보 인라인 편집 시작 — 해당 아이템의 현재 값을 편집 상태로 세팅
  function startShippingEdit(orderId, item) {
    setShippingEdit({ orderId, itemId: item.id, courier: item.courier || '', trackingNumber: item.tracking_number || '' });
  }

  // 택배정보 인라인 저장 — 낙관적 업데이트
  // 송장번호가 입력되면 백엔드가 workflow_step_id를 마지막 단계로 자동 설정하고 반환함 → UI에 즉시 반영
  async function saveShippingEdit() {
    if (!shippingEdit) return;
    const { orderId, itemId, courier, trackingNumber } = shippingEdit;
    // 낙관적으로 즉시 반영
    setOrders((prev) => prev.map((o) => {
      if (o.id !== orderId) return o;
      return { ...o, items: (o.items || []).map((i) =>
        i.id === itemId ? { ...i, courier: courier || null, tracking_number: trackingNumber || null } : i
      )};
    }));
    setShippingEdit(null);
    const res = await apiClient.patch(
      `${api.orders}/${orderId}/items/${itemId}/shipping`,
      { courier, tracking_number: trackingNumber }
    );
    if (!res.ok) {
      showToast('택배정보 저장 실패', 'error');
      fetchOrders();
      return;
    }
    // 택배정보 + 자동발주 UI 동기화 (백엔드 반환값으로 최신화)
    const updatedItem = res.data?.item;
    const callerName  = currentUser?.name || '관리자';
    setOrders((prev) => prev.map((o) => {
      if (o.id !== orderId) return o;
      const updatedItems = (o.items || []).map((i) => {
        if (i.id !== itemId) return i;
        // 송장 입력 시 미발주 상태면 자동 발주담당자 설정
        const autoOrdered = Boolean(trackingNumber && !i.is_ordered);
        return {
          ...i,
          courier:         updatedItem?.courier         ?? i.courier,
          tracking_number: updatedItem?.tracking_number ?? i.tracking_number,
          is_ordered:      autoOrdered ? true : i.is_ordered,
          ordered_by:      autoOrdered ? callerName    : i.ordered_by,
        };
      });
      return { ...o, items: updatedItems };
    }));
  }

  function openNew()    { setEditTarget(null);   setOrderModalOpen(true); }
  async function openEdit(o) {
    const res = await apiClient.get(`${api.orders}/${encodeURIComponent(o.id)}`);
    if (!res.ok || !res.data?.data) {
      showToast('최신 홍보물 주문 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.', 'error');
      return;
    }
    setEditTarget(res.data.data);
    setOrderModalOpen(true);
  }
  function askDelete(o) { setDeleteTarget(o);     setDeleteConfirm(true);  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiClient.del(`${api.orders}/${deleteTarget.id}`);
      setDeleteConfirm(false);
      setDeleteTarget(null);
      if (!res.ok) { showToast(res.data?.message || '삭제 실패', 'error'); return; }
      showToast('삭제되었습니다.', 'success');
      fetchOrders();
    } finally {
      setDeleting(false);
    }
  }

  return {
    screenMeta,
    isMobile,
    orders, filteredOrders, loadingOrders,
    promotionalItemsList, categoriesList,
    itemManageOpen, setItemManageOpen,
    orderModalOpen, setOrderModalOpen,
    editTarget,
    deleteTarget, setDeleteTarget, deleting, deleteConfirm, setDeleteConfirm,
    historyOrder, setHistoryOrder, historyData, historyLoading,
    searchQuery, setSearchQuery,
    shippingEdit, setShippingEdit,
    fetchOrders, fetchActiveItems,
    handleItemStepChange, handleItemOrderToggle, handleItemCancel,
    openHistory, startShippingEdit, saveShippingEdit,
    openNew, openEdit, askDelete, handleDelete,
  };
}
